import { useState, useCallback, useEffect, useMemo } from 'react';
import { parse, unparse } from 'papaparse';
import { useDropzone } from 'react-dropzone';
import { useAuthStore } from '../../store/authStore';
import { useCategoriesStore } from '../../store/categoriesStore';
import { useAccountsStore } from '../../store/accountsStore';
import { usePayeesStore } from '../../store/payeesStore';
import { useTagsStore } from '../../store/tagsStore';
import { useRulesStore } from '../../store/rulesStore';
import { toast } from "sonner";
import { Upload, X, FileText, Download, AlertTriangle, CheckCircle2, FileDown, Eye, EyeOff, ChevronDown, ChevronRight } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Combobox } from '@/components/ui/combobox';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { useQueryClient } from '@tanstack/react-query';
import { invalidateTransactions } from '@/utils/queryUtils';
import { useTransactionStore as useCoreTransactionStore } from '@flows.cash/core';
import { StyledModal, StyledModalSection, StyledModalButton } from '@/components/ui/styled-modal';
import { 
  checkBatchForDuplicates, 
  checkBatchForDuplicatesSmart,
  DuplicateCheckResult, 
  DEFAULT_DUPLICATE_CRITERIA,
  FAST_DUPLICATE_CRITERIA,
  DuplicateCheckMode
} from '@flows.cash/core';
import { supabase } from '@/lib/supabase';
import { transactionService } from '@flows.cash/core';
import { parseStatementPdf } from '@flows.cash/core';
import type { ParsedStatementResult, ParseStatementErrorCode } from '@flows.cash/types';
import StatementReviewTable from './StatementReviewTable';

interface Category {
  id: string;
  name: string;
  parent_id?: string;
  parent_name?: string;
  subcategories?: Category[];
}

interface Account {
  id: string;
  name: string;
  currency?: string;
}



interface ImportTransactionsProps {
  onClose: () => void;
  accountId?: string;
}

interface DateFormat {
  id: string;
  label: string;
  format: string;
  example: string;
}

// PDF statement rows arrive in the exact shape papaparse produces from a
// CSV, so everything downstream (payees, tags, rules, dedupe, commit) is the
// unchanged CSV pipeline. Date/Amount/Type come from the deterministic
// parser and are never touched by AI.
function mapStatementRowsToCsvShape(result: ParsedStatementResult): any[] {
  const { meta } = result;
  const periodNote = `${meta.bankName} statement${meta.statementDate ? ` ${meta.statementDate}` : ''}${
    meta.cardLast4 ? ` (card •••• ${meta.cardLast4})` : ''
  }`;
  return result.rows.map((r) => {
    const baseDescription = r.installment
      ? `${r.rawText} (installment ${r.installment.current}/${r.installment.total})`
      : r.rawText;
    return {
      Date: r.date,
      Description: r.description
        ? r.installment
          ? `${r.description} (installment ${r.installment.current}/${r.installment.total})`
          : r.description
        : baseDescription,
      Category: r.categoryName ?? '',
      Amount: r.amount.toString(),
      Type: r.type,
      Account: '', // filled in when the user picks the statement's account
      Payee: r.payee ?? (r.type === 'transfer' ? '' : r.rawText),
      Tags: r.tags.join(', '),
      Notes: [
        // Keep the printed merchant string when enrichment cleaned it up.
        r.payee || r.description ? `Merchant: ${r.rawText}` : null,
        ...r.notes,
        r.sectionHint ? `Statement section: ${r.sectionHint}` : null,
        periodNote,
      ]
        .filter(Boolean)
        .join(' | '),
    };
  });
}

const STATEMENT_ERROR_MESSAGES: Record<ParseStatementErrorCode, string> = {
  rate_limited: 'Too many statement uploads — please wait a minute and try again.',
  file_too_large: 'This PDF is too large. Statements are usually well under 6 MB.',
  invalid_pdf: "This file doesn't look like a valid PDF.",
  unsupported_statement:
    "This statement format isn't supported yet. You can export a CSV from your bank and import that instead.",
  statement_unavailable: 'Statement parsing is temporarily unavailable. Please try again.',
};

const DATE_FORMATS: DateFormat[] = [
  // Date only formats
  { id: 'DD/MM/YYYY', label: 'DD/MM/YYYY', format: 'DD/MM/YYYY', example: '31/12/2023' },
  { id: 'DD.MM.YYYY', label: 'DD.MM.YYYY', format: 'DD.MM.YYYY', example: '31.12.2023' },
  { id: 'MM/DD/YYYY', label: 'MM/DD/YYYY', format: 'MM/DD/YYYY', example: '12/31/2023' },
  { id: 'MM.DD.YYYY', label: 'MM.DD.YYYY', format: 'MM.DD.YYYY', example: '12.31.2023' },
  { id: 'YYYY/MM/DD', label: 'YYYY/MM/DD', format: 'YYYY/MM/DD', example: '2023/12/31' },
  { id: 'YYYY.MM.DD', label: 'YYYY.MM.DD', format: 'YYYY.MM.DD', example: '2023.12.31' },
  { id: 'MM-DD-YYYY', label: 'MM-DD-YYYY', format: 'MM-DD-YYYY', example: '12-31-2023' },
  { id: 'DD-MM-YYYY', label: 'DD-MM-YYYY', format: 'DD-MM-YYYY', example: '31-12-2023' },
  { id: 'YYYY-MM-DD', label: 'YYYY-MM-DD', format: 'YYYY-MM-DD', example: '2023-12-31' },
  
  // Date with 12-hour time formats
  { id: 'DD/MM/YYYY hh:mm A', label: 'DD/MM/YYYY hh:mm AM/PM', format: 'DD/MM/YYYY hh:mm A', example: '31/12/2023 02:30 PM' },
  { id: 'DD.MM.YYYY hh:mm A', label: 'DD.MM.YYYY hh:mm AM/PM', format: 'DD.MM.YYYY hh:mm A', example: '31.12.2023 02:30 PM' },
  { id: 'MM/DD/YYYY hh:mm A', label: 'MM/DD/YYYY hh:mm AM/PM', format: 'MM/DD/YYYY hh:mm A', example: '12/31/2023 02:30 PM' },
  { id: 'MM.DD.YYYY hh:mm A', label: 'MM.DD.YYYY hh:mm AM/PM', format: 'MM.DD.YYYY hh:mm A', example: '12.31.2023 02:30 PM' },
  { id: 'YYYY/MM/DD hh:mm A', label: 'YYYY/MM/DD hh:mm AM/PM', format: 'YYYY/MM/DD hh:mm A', example: '2023/12/31 02:30 PM' },
  
  // Date with 24-hour time formats
  { id: 'DD/MM/YYYY HH:mm', label: 'DD/MM/YYYY HH:mm (24h)', format: 'DD/MM/YYYY HH:mm', example: '31/12/2023 14:30' },
  { id: 'DD.MM.YYYY HH:mm', label: 'DD.MM.YYYY HH:mm (24h)', format: 'DD.MM.YYYY HH:mm', example: '31.12.2023 14:30' },
  { id: 'MM/DD/YYYY HH:mm', label: 'MM/DD/YYYY HH:mm (24h)', format: 'MM/DD/YYYY HH:mm', example: '12/31/2023 14:30' },
  { id: 'MM.DD.YYYY HH:mm', label: 'MM.DD.YYYY HH:mm (24h)', format: 'MM.DD.YYYY HH:mm', example: '12.31.2023 14:30' },
  { id: 'YYYY/MM/DD HH:mm', label: 'YYYY/MM/DD HH:mm (24h)', format: 'YYYY/MM/DD HH:mm', example: '2023/12/31 14:30' },
  
  // ISO formats
  { id: 'YYYY-MM-DDTHH:mm:ss', label: 'ISO Format', format: 'YYYY-MM-DDTHH:mm:ss', example: '2023-12-31T14:30:00' },
  { id: 'YYYY-MM-DDTHH:mm:ssZ', label: 'ISO Format with Timezone', format: 'YYYY-MM-DDTHH:mm:ssZ', example: '2023-12-31T14:30:00Z' }
];

export default function ImportTransactions({ onClose, accountId }: ImportTransactionsProps) {
  const { user } = useAuthStore();
  const coreTransactionStore = useCoreTransactionStore();
  const { categories, fetchCategories } = useCategoriesStore();
  const { accounts } = useAccountsStore();
  const { fetchPayees } = usePayeesStore();
  const { fetchTags } = useTagsStore();
  const queryClient = useQueryClient();
  const [importing, setImporting] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<any[] | null>(null);
  const [selectedDateFormat, setSelectedDateFormat] = useState('DD/MM/YYYY HH:mm');
  const [detectedDateFormat, setDetectedDateFormat] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [importResults, setImportResults] = useState<{
    imported: any[];
    skipped: { data: any; reason: string }[];
    total: number;
  }>({ imported: [], skipped: [], total: 0 });
  const [unmatchedCategories, setUnmatchedCategories] = useState<Set<string>>(new Set());
  const [importCompleted, setImportCompleted] = useState(false);
  const [importedCount, setImportedCount] = useState(0);
  // Duplicate detection state
  const [duplicateResults, setDuplicateResults] = useState<DuplicateCheckResult[]>([]);
  const [showDuplicatePrompt, setShowDuplicatePrompt] = useState(false);
  const [showDuplicatesInReport, setShowDuplicatesInReport] = useState(false);
  const [pendingTransactions, setPendingTransactions] = useState<any[]>([]);
  const [originalDataMap, setOriginalDataMap] = useState<Map<number, any>>(new Map());
  const [duplicateCheckMode, setDuplicateCheckMode] = useState<DuplicateCheckMode>(DuplicateCheckMode.FAST);
  const [skipDuplicateCheck, setSkipDuplicateCheck] = useState(false);

  const [invalidRuleCategoryIds, setInvalidRuleCategoryIds] = useState<Set<string>>(new Set());
  // PDF statement import state (null when importing a CSV)
  const [statementResult, setStatementResult] = useState<ParsedStatementResult | null>(null);
  // Account chosen in the dialog. For statements it's the account being
  // imported into; for CSVs it's the fallback for rows whose Account column
  // doesn't name a real account (bank exports never carry our account names).
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [reconciliationOverride, setReconciliationOverride] = useState(false);
  const [currencyAck, setCurrencyAck] = useState(false);
  const [parsingStatement, setParsingStatement] = useState(false);
  const [showDateFormat, setShowDateFormat] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  // Review-screen corrections: payee (lowercased key) → category name. When
  // rememberCorrections is on, these persist to the payees table after
  // import so the server-side history layer auto-applies them next time.
  const [rememberedCategories, setRememberedCategories] = useState<
    Map<string, { payee: string; categoryName: string }>
  >(new Map());
  const [rememberCorrections, setRememberCorrections] = useState(true);

  useEffect(() => {
    if (user?.id) {
      fetchPayees(user.id);
      fetchTags(user.id);
    }
  }, [user?.id, fetchPayees, fetchTags]);

  // Function to detect date format from CSV data
  const detectDateFormat = (data: any[]): string | null => {
    if (!data || data.length === 0) return null;

    // Get a sample of date values from the first few rows
    const sampleSize = Math.min(10, data.length);
    const dateValues: string[] = [];
    
    for (let i = 0; i < sampleSize; i++) {
      const row = data[i];
      if (row.Date && typeof row.Date === 'string') {
        dateValues.push(row.Date.trim());
      }
    }

    if (dateValues.length === 0) return null;

    console.log('Date values to analyze:', dateValues);

    // EARLY CHECK: If all dates are dot-separated, immediately prefer DD.MM format
    const allDotSeparated = dateValues.every(date => /^\d{1,2}\.\d{1,2}\.\d{4}(\s+\d{1,2}:\d{2})?/.test(date));
    if (allDotSeparated) {
      console.log('All dates are dot-separated - checking for DD.MM preference');
      
      // Check if any date has first part > 12 (definitely day-first)
      const hasDefinitelyDayFirst = dateValues.some(date => {
        const parts = date.split(' ')[0].split('.');
        return parseInt(parts[0]) > 12;
      });
      
      if (hasDefinitelyDayFirst) {
        console.log('Found date with day > 12, definitely DD.MM format');
        const hasTime = dateValues.some(date => date.includes(' '));
        return hasTime ? 'DD.MM.YYYY HH:mm' : 'DD.MM.YYYY';
      }
      
      // If no definitive indicator, default to DD.MM for dot-separated dates
      console.log('No definitive indicator - defaulting to DD.MM for dot-separated dates');
      const hasTime = dateValues.some(date => date.includes(' '));
      return hasTime ? 'DD.MM.YYYY HH:mm' : 'DD.MM.YYYY';
    }

    // Test each format against the sample dates with better validation
    const formatScores: { [key: string]: number } = {};
    
    for (const format of DATE_FORMATS) {
      let successCount = 0;
      let validDates = 0;
      
      for (const dateValue of dateValues) {
        try {
          const parsedDate = parseDate(dateValue, format);
          if (parsedDate && !isNaN(parsedDate.getTime())) {
            // Additional validation: check if the parsed date is reasonable
            const year = parsedDate.getFullYear();
            
            // Validate year range
            if (year >= 1900 && year <= 2100) {
                            // Additional validation for ambiguous formats
              // For dot-separated dates like "4.08.2025", heavily prefer DD.MM format
              if (dateValue.includes('.')) {
                const parts = dateValue.split(' ')[0].split('.');
                if (parts.length === 3) {
                  const [p1, p2] = parts.map(Number);
                  
                  // If first part > 12, it's definitely day-first format
                  if (p1 > 12 && format.id.startsWith('MM')) {
                    continue; // Skip MM.DD formats when first part > 12
                  }
                  
                  // If second part > 12, it's definitely month-first format
                  if (p2 > 12 && format.id.startsWith('DD')) {
                    continue; // Skip DD.MM formats when second part > 12
                  }
                  
                  // For dot-separated dates, STRONGLY prefer DD.MM.YYYY format
                  if (format.id.includes('DD.MM.YYYY') && p1 <= 31 && p2 <= 12) {
                    successCount += 5; // Give heavy weight to DD.MM format for dot-separated dates
                  }
                  
                  // Discourage MM.DD format for dot-separated dates
                  if (format.id.includes('MM.DD.YYYY') && p1 <= 12 && p2 <= 31) {
                    successCount -= 1; // Reduce weight for MM.DD format
                  }
                }
              }
              
              successCount++;
              validDates++;
            }
          }
        } catch (error) {
          // Format doesn't match this date
          continue;
        }
      }
      
      // Calculate success rate
      const successRate = validDates > 0 ? successCount / dateValues.length : 0;
      formatScores[format.id] = successRate;
      
      console.log(`Format ${format.id}: ${successCount}/${dateValues.length} (score: ${successRate.toFixed(2)})`);
    }

    // Find the format with the highest success rate
    let bestFormat: string | null = null;
    let highestScore = 0;
    
    for (const [formatId, score] of Object.entries(formatScores)) {
      if (score > highestScore && score >= 0.7) { // Require at least 70% success rate
        highestScore = score;
        bestFormat = formatId;
      }
    }

    // If we found a good match, return it
    if (bestFormat && highestScore >= 0.7) {
      console.log(`Auto-detected date format: ${bestFormat} (${Math.round(highestScore * 100)}% confidence)`);
      return bestFormat;
    }

        // Enhanced pattern-based heuristics with immediate dot-preference
    if (dateValues.length > 0) {
      const firstDate = dateValues[0];
      console.log('Using pattern-based detection for:', firstDate);
      
      // PRIORITY 1: Handle dot-separated dates first (European format preference)
      if (/^\d{1,2}\.\d{1,2}\.\d{4}(\s+\d{1,2}:\d{2})?/.test(firstDate)) {
        console.log('Detected dot-separated date - defaulting to DD.MM.YYYY format');
        const parts = firstDate.split(' ')[0].split('.');
        const [p1, p2] = parts.map(Number);
        
        // If first part > 12, it's definitely day-first
        if (p1 > 12) {
          console.log('First part > 12, definitely DD.MM format');
          return firstDate.includes(' ') ? 'DD.MM.YYYY HH:mm' : 'DD.MM.YYYY';
        }
        
        // If second part > 12, it's definitely month-first
        if (p2 > 12) {
          console.log('Second part > 12, definitely MM.DD format');
          return firstDate.includes(' ') ? 'MM.DD.YYYY HH:mm' : 'MM.DD.YYYY';
        }
        
        // For ambiguous cases with dots, ALWAYS prefer European DD.MM format
        console.log('Ambiguous dot-separated date, choosing DD.MM format by default');
        return firstDate.includes(' ') ? 'DD.MM.YYYY HH:mm' : 'DD.MM.YYYY';
      }
      
      // PRIORITY 2: ISO formats
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(firstDate)) {
        return 'YYYY-MM-DDTHH:mm:ss';
      }
      
      if (/^\d{4}-\d{2}-\d{2}$/.test(firstDate)) {
        return 'YYYY-MM-DD';
      }
      
      // PRIORITY 3: Handle slash-separated dates
      if (/^\d{1,2}\/\d{1,2}\/\d{4}(\s+\d{1,2}:\d{2})?/.test(firstDate)) {
        const parts = firstDate.split(' ')[0].split('/');
        const [p1, p2] = parts.map(Number);
        
        // If first part > 12, it's definitely day-first
        if (p1 > 12) {
          return firstDate.includes(' ') ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY';
        }
        
        // If second part > 12, it's definitely month-first
        if (p2 > 12) {
          return firstDate.includes(' ') ? 'MM/DD/YYYY HH:mm' : 'MM/DD/YYYY';
        }
        
        // For ambiguous cases with slashes, prefer DD/MM format (international standard)
        return firstDate.includes(' ') ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY';
      }
      
      // PRIORITY 4: Handle dash-separated dates
      if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(firstDate)) {
        return 'DD-MM-YYYY';
      }
    }

    console.log('No suitable format detected');
    return null;
  };





  // Helper to get a random color for new tags
  const getRandomColor = (): string => {
    const colors = ['#6366F1', '#EC4899', '#14B8A6', '#F59E0B', '#8B5CF6', '#EF4444', '#22C55E', '#3B82F6'];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  // Function to find the best matching category
  const findBestMatchingCategory = (categoryString: string): Category | null => {
    if (!categoryString || !categories || categories.length === 0) return null;
    
    // Normalize the input category string
    const normalizedCategoryString = categoryString.trim().toLowerCase();
    
    // First try: exact match (case insensitive)
    const exactMatch = categories.find((c: Category) => 
      c.name.toLowerCase() === normalizedCategoryString
    );
    if (exactMatch) return exactMatch;
    
    // Check if the category has subcategories structure
    const hasSubcategories = categories.some((c: Category) => c.subcategories && c.subcategories.length > 0);
    
    // Handle "Category - Subcategory" format
    if (categoryString.includes(' - ')) {
      const [mainCategory, subCategory] = categoryString.split(' - ').map(s => s.trim().toLowerCase());
      
      // Try to match subcategory first (more specific)
      if (hasSubcategories) {
        // Look through all categories and their subcategories
        for (const cat of categories) {
          if (!cat.subcategories?.length) continue;
          
          const matchingSubcat = cat.subcategories.find((subCat: Category) => 
            subCat.name.toLowerCase() === subCategory &&
            cat.name.toLowerCase() === mainCategory
          );
          
          if (matchingSubcat) return matchingSubcat;
        }
      } else {
        // If we don't have subcategories structure, try to find a category with parent_id
        const subCategoryMatch = categories.find((c: Category) => 
          c.name.toLowerCase() === subCategory && 
          c.parent_name?.toLowerCase() === mainCategory
        );
        if (subCategoryMatch) return subCategoryMatch;
      }
      
      // If no subcategory match, try to match main category
      const mainCategoryMatch = categories.find((c: Category) => 
        c.name.toLowerCase() === mainCategory
      );
      if (mainCategoryMatch) return mainCategoryMatch;
    }
    
    // Handle "Category/Subcategory" format
    if (categoryString.includes('/')) {
      const [mainCategory, subCategory] = categoryString.split('/').map(s => s.trim().toLowerCase());
      
      // Try to match subcategory first (more specific)
      if (hasSubcategories) {
        // Look through all categories and their subcategories
        for (const cat of categories) {
          if (!cat.subcategories?.length) continue;
          
          const matchingSubcat = cat.subcategories.find((subCat: Category) => 
            subCat.name.toLowerCase() === subCategory &&
            cat.name.toLowerCase() === mainCategory
          );
          
          if (matchingSubcat) return matchingSubcat;
        }
      } else {
        // If we don't have subcategories structure, try to find a category with parent_id
        const subCategoryMatch = categories.find((c: Category) => 
          c.name.toLowerCase() === subCategory && 
          c.parent_name?.toLowerCase() === mainCategory
        );
        if (subCategoryMatch) return subCategoryMatch;
      }
      
      // If no subcategory match, try to match main category
      const mainCategoryMatch = categories.find((c: Category) => 
        c.name.toLowerCase() === mainCategory
      );
      if (mainCategoryMatch) return mainCategoryMatch;
    }
    
    // Fuzzy match on category words
    const categoryWords = normalizedCategoryString.split(/[\s\-\/]+/).filter(Boolean);
    
    // First, try to match subcategories (they are more specific)
    if (hasSubcategories) {
      // Look through all categories and their subcategories
      for (const cat of categories) {
        if (!cat.subcategories?.length) continue;
        
        for (const subCat of cat.subcategories) {
          const subCatWords = subCat.name.toLowerCase().split(/[\s\-\/]+/).filter(Boolean);
          const matches = subCatWords.some((s: string) => categoryWords.includes(s));
          if (matches) return subCat;
        }
      }
    }
    
    // Try to match categories with parent_id (subcategories)
    const subcategories = categories.filter((c: Category) => c.parent_id);
    for (const subcategory of subcategories) {
      const subcategoryWords = subcategory.name.toLowerCase().split(/[\s\-\/]+/).filter(Boolean);
      const matches = subcategoryWords.some((s: string) => categoryWords.includes(s));
      if (matches) return subcategory;
    }
    
    // If no subcategory match, try to match main categories
    const mainCategories = categories.filter((c: Category) => !c.parent_id);
    for (const mainCategory of mainCategories) {
      const mainCategoryWords = mainCategory.name.toLowerCase().split(/[\s\-\/]+/).filter(Boolean);
      const matches = mainCategoryWords.some((s: string) => categoryWords.includes(s));
      if (matches) return mainCategory;
    }
    
    // Partial word match
    for (const catWord of categoryWords) {
      if (catWord.length < 3) continue; // Skip very short words
      
      // Try subcategories first
      if (hasSubcategories) {
        // Look through all categories and their subcategories
        for (const cat of categories) {
          if (!cat.subcategories?.length) continue;
          
          for (const subCat of cat.subcategories) {
            if (subCat.name.toLowerCase().includes(catWord)) {
              return subCat;
            }
          }
        }
      }
      
      // Try categories with parent_id
      for (const subcategory of subcategories) {
        if (subcategory.name.toLowerCase().includes(catWord)) {
          return subcategory;
        }
      }
      
      // Then try main categories
      for (const mainCategory of mainCategories) {
        if (mainCategory.name.toLowerCase().includes(catWord)) {
          return mainCategory;
        }
      }
    }
    
    // If all else fails, try a more aggressive fuzzy match
    for (const cat of categories) {
      const catName = cat.name.toLowerCase();
      const similarity = calculateStringSimilarity(catName, normalizedCategoryString);
      if (similarity > 0.7) { // 70% similarity threshold
        return cat;
      }
    }
    
    return null; // No match found
  };

  // Helper function to calculate string similarity (Levenshtein distance based)
  const calculateStringSimilarity = (str1: string, str2: string): number => {
    if (str1 === str2) return 1.0;
    if (str1.length === 0 || str2.length === 0) return 0.0;
    
    const len1 = str1.length;
    const len2 = str2.length;
    
    // Simple implementation of Levenshtein distance
    const matrix: number[][] = Array(len1 + 1).fill(null).map(() => Array(len2 + 1).fill(0));
    
    for (let i = 0; i <= len1; i++) matrix[i][0] = i;
    for (let j = 0; j <= len2; j++) matrix[0][j] = j;
    
    for (let i = 1; i <= len1; i++) {
      for (let j = 1; j <= len2; j++) {
        const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
        matrix[i][j] = Math.min(
          matrix[i - 1][j] + 1,      // deletion
          matrix[i][j - 1] + 1,      // insertion
          matrix[i - 1][j - 1] + cost // substitution
        );
      }
    }
    
    const distance = matrix[len1][len2];
    const maxLen = Math.max(len1, len2);
    
    // Return similarity as a value between 0 and 1
    return 1 - distance / maxLen;
  };

  // Helper function to create payees in bulk
  const createPayeesInBulk = async (payeeNames: string[], userId: string): Promise<Map<string, string>> => {
    const payeeMap = new Map<string, string>();
    
    if (payeeNames.length === 0) return payeeMap;
    
    console.log(`Creating ${payeeNames.length} payees in bulk...`);
    
    try {
      // First, check which payees already exist
      const { data: existingPayees, error: fetchError } = await supabase
        .from('payees')
        .select('id, name')
        .eq('user_id', userId)
        .in('name', payeeNames);
      
      if (fetchError) throw fetchError;
      
      // Map existing payees
      (existingPayees || []).forEach(payee => {
        payeeMap.set(payee.name.toLowerCase(), payee.id);
      });
      
      // Find payees that don't exist yet
      const existingNames = new Set((existingPayees || []).map(p => p.name.toLowerCase()));
      const newPayeeNames = payeeNames.filter(name => !existingNames.has(name.toLowerCase()));
      
      if (newPayeeNames.length > 0) {
        console.log(`Creating ${newPayeeNames.length} new payees...`);
        
        // Prepare payees for bulk insert
        const payeesToCreate = newPayeeNames.map(name => ({
          name: name,
          user_id: userId,
          type: 'business',
          total_transactions: 0,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }));
        
        // Use bulk insert
        const { data: newPayees, error: insertError } = await supabase
          .from('payees')
          .insert(payeesToCreate)
          .select('id, name');
        
        if (insertError) throw insertError;
        
        // Map the newly created payees
        (newPayees || []).forEach(payee => {
          payeeMap.set(payee.name.toLowerCase(), payee.id);
        });
        
        console.log(`Successfully created ${newPayees?.length || 0} new payees`);
      }
      
      // Refresh payees in the store
      await fetchPayees(userId, true);
      
    } catch (error) {
      console.error('Error creating payees in bulk:', error);
      throw error;
    }
    
    return payeeMap;
  };

  // Helper function to create tags in bulk
  const createTagsInBulk = async (tagNames: string[], userId: string): Promise<Map<string, string>> => {
    const tagMap = new Map<string, string>();
    
    if (tagNames.length === 0) return tagMap;
    
    console.log(`Creating ${tagNames.length} tags in bulk...`);
    
    try {
      // First, check which tags already exist
      const { data: existingTags, error: fetchError } = await supabase
        .from('tags')
        .select('id, name')
        .eq('user_id', userId)
        .in('name', tagNames);
      
      if (fetchError) throw fetchError;
      
      // Map existing tags
      (existingTags || []).forEach(tag => {
        tagMap.set(tag.name.toLowerCase(), tag.id);
      });
      
      // Find tags that don't exist yet
      const existingNames = new Set((existingTags || []).map(t => t.name.toLowerCase()));
      const newTagNames = tagNames.filter(name => !existingNames.has(name.toLowerCase()));
      
      if (newTagNames.length > 0) {
        console.log(`Creating ${newTagNames.length} new tags...`);
        
        // Prepare tags for bulk insert
        const tagsToCreate = newTagNames.map(name => ({
          name: name,
          user_id: userId,
          color: getRandomColor(),
          total_transactions: 0,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }));
        
        // Use bulk insert
        const { data: newTags, error: insertError } = await supabase
          .from('tags')
          .insert(tagsToCreate)
          .select('id, name');
        
        if (insertError) throw insertError;
        
        // Map the newly created tags
        (newTags || []).forEach(tag => {
          tagMap.set(tag.name.toLowerCase(), tag.id);
        });
        
        console.log(`Successfully created ${newTags?.length || 0} new tags`);
      }
      
      // Refresh tags in the store
      await fetchTags(userId, true);
      
    } catch (error) {
      console.error('Error creating tags in bulk:', error);
      throw error;
    }
    
    return tagMap;
  };

  // Helper function to validate categories in bulk
  const validateCategoriesInBulk = async (categoryIds: string[], userId: string): Promise<Set<string>> => {
    const validCategoryIds = new Set<string>();
    
    if (categoryIds.length === 0) return validCategoryIds;
    
    try {
      const { data: validCategories, error } = await supabase
        .from('categories')
        .select('id')
        .eq('user_id', userId)
        .in('id', categoryIds);
      
      if (error) {
        console.error('Error validating categories in bulk:', error);
        return validCategoryIds;
      }
      
      (validCategories || []).forEach(category => {
        validCategoryIds.add(category.id);
      });
      
    } catch (error) {
      console.error('Error validating categories in bulk:', error);
    }
    
    return validCategoryIds;
  };

  const processTransactions = async (data: any[], userId: string) => {
    const imported: any[] = [];
    const skipped: { data: any; reason: string }[] = [];
    const total = data.length;
    const newErrors: string[] = [];
    const localUnmatchedCategories: Set<string> = new Set();
    
    // Create a mapping from row index to original CSV data for error reporting
    const dataMap: Map<number, any> = new Map();
    data.forEach((row, index) => {
      dataMap.set(index, row);
    });
    setOriginalDataMap(dataMap);
    
    // Reset imported count
    setImportedCount(0);
    
    // Prepared transactions for bulk processing
    const preparedTransactions: any[] = [];
    
    // Process all data locally first
    toast.loading('Processing data...', { id: 'import-progress' });
    
    try {
      // Make sure we have the latest categories and rules before processing
      await Promise.all([
        fetchCategories(userId),
        useRulesStore.getState().fetchRules()
      ]);
      
      // STEP 1: Validate and prepare all transactions (25% progress)
      console.log('🚀 STEP 1: Validating and preparing transactions...');
      
      for (let i = 0; i < data.length; i++) {
        const row = data[i];
        try {
          // Update progress
          const currentProgress = Math.round(((i + 1) / total) * 25); // First 25% for validation
          setProgress(currentProgress);
          
          // Handle account - if account not found, skip this transaction
          let account: Account | undefined;
          if (accountId) {
            account = accounts.find((a: Account) => a.id === accountId);
          } else {
            // A row's own Account column wins when it names a real account,
            // so multi-account CSVs keep working; the account picked in the
            // dialog covers every other row.
            if (row.Account) {
              account = accounts.find((a: Account) => a.name === row.Account);
            }
            if (!account && selectedAccountId) {
              account = accounts.find((a: Account) => a.id === selectedAccountId);
            }
          }

          if (!account) {
            const reason = row.Account
              ? `Account "${row.Account}" not found — pick an account in the import dialog to use for these rows`
              : 'No account for this row — pick an account in the import dialog';
            skipped.push({ data: row, reason });
            continue;
          }

          // Find and validate the category using the new validation function
          let categoryId = '';
          let originalCategory = '';
          
          if (row.Category) {
            originalCategory = row.Category;
            const categoryMatch = findBestMatchingCategory(row.Category);
            if (categoryMatch) {
              categoryId = categoryMatch.id;
            } else {
              localUnmatchedCategories.add(row.Category);
            }
          }
          
          // Parse amount and determine type
          let amount = parseFloat(row.Amount?.toString().replace(/[^0-9.-]+/g, ''));
          if (isNaN(amount)) {
            const reason = `Invalid amount: ${row.Amount}`;
            skipped.push({ data: row, reason });
            continue;
          }

          // Determine transaction type
          let transactionType: 'expense' | 'income' | 'transfer' = 'expense';
          if (row.Type && typeof row.Type === 'string') {
            const typeStr = row.Type.toLowerCase().trim();
            if (typeStr === 'income' || typeStr === 'revenue' || typeStr === 'deposit') {
              transactionType = 'income';
            } else if (typeStr === 'expense' || typeStr === 'withdrawal' || typeStr === 'payment') {
              transactionType = 'expense';
            } else if (typeStr === 'transfer') {
              // Credit-card payments from statement imports: money moved
              // between the user's own accounts, never income or expense.
              transactionType = 'transfer';
            }
          } else {
            // Fallback to determining by amount sign
            transactionType = amount < 0 ? 'expense' : 'income';
          }

          // Parse date
          const format = DATE_FORMATS.find(f => f.id === selectedDateFormat);
          if (!format) {
            const reason = 'Invalid date format selected in settings';
            skipped.push({ data: row, reason });
            continue;
          }

          let transactionDate: Date;
          try {
            transactionDate = parseDate(row.Date, format);
          } catch (error) {
            const reason = error instanceof Error ? error.message : 'Invalid date format';
            skipped.push({ data: row, reason });
            continue;
          }

          // Create transaction object
          const transactionData: any = {
            user_id: userId,
            date: transactionDate instanceof Date ? transactionDate.toISOString() : transactionDate,
            amount: transactionType === 'expense' ? -Math.abs(amount) : Math.abs(amount),
            type: transactionType,
            account_id: account.id,
            tags: [], // Will be filled later after tag processing
            description: row.Description?.trim() || '',
            notes: row.Notes?.trim() || '',
            status: 'completed',
            currency: account.currency || 'USD',
            originalCategory: originalCategory,
            // Store original payee and tags data for later processing
            _originalPayee: row.Payee || null,
            _originalTags: row.Tags || null,
            _rowIndex: i,
          };

          // Only add category_id if it's a valid non-empty string
          if (categoryId && categoryId.trim() !== '') {
            transactionData.category_id = categoryId;
          }

          // Add to prepared transactions list
          preparedTransactions.push(transactionData);
        } catch (error) {
          console.error('Error processing transaction:', error instanceof Error ? error.message : error);
          skipped.push({
            data: row, 
            reason: error instanceof Error ? error.message : 'Unknown error' 
          });
        }
      }
      
      console.log(`✅ STEP 1 Complete: ${preparedTransactions.length} transactions prepared, ${skipped.length} skipped`);
      
      // STEP 2: Bulk create payees and tags (25-35% progress)
      console.log('🚀 STEP 2: Creating payees and tags in bulk...');
      toast.loading('Creating payees and tags...', { id: 'import-progress' });
      
      // Extract unique payee names
      const uniquePayeeNames = Array.from(new Set(
        preparedTransactions
          .map(tx => tx._originalPayee)
          .filter((payee): payee is string => Boolean(payee && payee.trim()))
      ));
      
      // Extract unique tag names
      const uniqueTagNames = Array.from(new Set(
        preparedTransactions
          .flatMap(tx => {
            if (!tx._originalTags) return [];
            return tx._originalTags.split(',')
              .map((t: string) => t.trim())
              .filter(Boolean);
          })
      ));
      
      console.log(`Found ${uniquePayeeNames.length} unique payees and ${uniqueTagNames.length} unique tags`);
      
      // Create payees and tags in bulk
      const [payeeMap, tagMap] = await Promise.all([
        createPayeesInBulk(uniquePayeeNames, userId),
        createTagsInBulk(uniqueTagNames, userId)
      ]);
      
      setProgress(35);
      console.log(`✅ STEP 2 Complete: Created ${payeeMap.size} payees and ${tagMap.size} tags`);
      
      // STEP 3: Validate categories in bulk (35-40% progress)
      console.log('🚀 STEP 3: Validating categories in bulk...');
      toast.loading('Validating categories...', { id: 'import-progress' });
      
      // Get category IDs from import data
      const importCategoryIds = Array.from(new Set(
        preparedTransactions
          .map(tx => tx.category_id)
          .filter((id): id is string => Boolean(id))
      ));
      
      // Get ALL user category IDs so rules can set any valid category
      const { data: allUserCategories, error: categoryError } = await supabase
        .from('categories')
        .select('id')
        .eq('user_id', userId);
      
      if (categoryError) {
        console.error('Error fetching user categories:', categoryError);
        throw categoryError;
      }
      
      // Create valid category IDs set from all user categories (not just import data)
      const validCategoryIds = new Set(
        (allUserCategories || []).map(cat => cat.id)
      );
      
      // Also validate the import category IDs separately for import validation
      const validImportCategoryIds = await validateCategoriesInBulk(importCategoryIds, userId);
      
      setProgress(40);
      console.log(`✅ STEP 3 Complete: Validated ${validCategoryIds.size} total user categories, ${validImportCategoryIds.size} from import data`);
      
      // STEP 4: Apply payees, tags, and validate categories (40-50% progress)
      console.log('🚀 STEP 4: Applying payees, tags, and category validation...');
      toast.loading('Preparing final transactions...', { id: 'import-progress' });
      
      const finalTransactions: any[] = [];
      
      for (let i = 0; i < preparedTransactions.length; i++) {
        const tx = preparedTransactions[i];
        
        // Process tags for this transaction
        if (tx._originalTags) {
          const tagIds: string[] = [];
          const tagNames = tx._originalTags.split(',').map((t: string) => t.trim()).filter(Boolean);
          
          tagNames.forEach((tagName: string) => {
            const tagId = tagMap.get(tagName.toLowerCase());
            if (tagId) {
              tagIds.push(tagId);
            }
          });
          
          tx.tags = tagIds;
        }
        
        // Process payee for this transaction
        if (tx._originalPayee && tx._originalPayee.trim() !== '') {
          const payeeId = payeeMap.get(tx._originalPayee.toLowerCase());
          if (payeeId) {
            tx.payee_id = payeeId;
            tx.payee_name = tx._originalPayee;
          }
        }
        
        // Validate category exists
        if (tx.category_id && !validCategoryIds.has(tx.category_id)) {
          console.warn(`Category ${tx.category_id} not found in database, removing from transaction`);
          delete tx.category_id;
          if (tx.originalCategory) {
            localUnmatchedCategories.add(tx.originalCategory);
          }
        }
        
        // Apply rules to the transaction
        try {
          const processedTransaction = useRulesStore.getState().applyRules({
            ...tx,
            description: tx.description || '',
            notes: tx.notes || '',
            payee: tx.payee_id,
            categoryId: tx.category_id
          });
          
          // Update transaction fields with processed values
          if (processedTransaction.categoryId && processedTransaction.categoryId !== tx.category_id) {
            // Validate the category ID from rules before applying it
            if (validCategoryIds.has(processedTransaction.categoryId)) {
              tx.category_id = processedTransaction.categoryId;
            } else {
              // Track invalid rule category IDs for summary reporting
              setInvalidRuleCategoryIds(prev => new Set([...prev, processedTransaction.categoryId]));
            }
          }
          
          if (processedTransaction.payee && processedTransaction.payee !== tx.payee_id) {
            tx.payee_id = processedTransaction.payee;
          }
          
          if (processedTransaction.tags) {
            tx.tags = processedTransaction.tags;
          }
          
          if (processedTransaction.notes && processedTransaction.notes !== tx.notes) {
            tx.notes = processedTransaction.notes;
          }
          
          if (processedTransaction.description && processedTransaction.description !== tx.description) {
            tx.description = processedTransaction.description;
          }
        } catch (error) {
          console.error('Error applying rules:', error);
        }
        
        // Remove temp properties and prepare final transaction
        const finalTransaction: any = {
          account_id: tx.account_id,
          payee_id: tx.payee_id,
          payee_name: tx.payee_name,
          tags: tx.tags || [],
          notes: tx.notes || '',
          status: tx.status || 'completed',
          type: tx.type || 'expense',
          amount: tx.amount,
          date: tx.date,
          description: tx.description || '',
          currency: tx.currency || 'USD',
          user_id: userId,
          originalCategory: tx.originalCategory,
          _rowIndex: tx._rowIndex
        } as any;
        
        // Only add category_id if it exists and is not empty
        if (tx.category_id && tx.category_id.trim() !== '') {
          finalTransaction.category_id = tx.category_id;
        }
        
        finalTransactions.push(finalTransaction);
        
        // Update progress (40-50%)
        const currentProgress = 40 + Math.round(((i + 1) / preparedTransactions.length) * 10);
        setProgress(currentProgress);
      }
      
      console.log(`✅ STEP 4 Complete: ${finalTransactions.length} transactions ready for import`);
      
      // STEP 5: Check for duplicates (50-65% progress)
      let duplicateCheckResults: DuplicateCheckResult[] = [];
      
      console.log('🚀 STEP 5: Checking for duplicates...');
      
      // Determine the target account for duplicate checking
      let targetAccountForDuplicateCheck = accountId;
      
      if (!targetAccountForDuplicateCheck && finalTransactions.length > 0) {
        const uniqueAccountIds = new Set(finalTransactions.map(t => t.account_id).filter(Boolean));
        if (uniqueAccountIds.size === 1) {
          targetAccountForDuplicateCheck = Array.from(uniqueAccountIds)[0];
          console.log('📝 Determined target account from transactions:', targetAccountForDuplicateCheck);
        } else {
          console.log('📝 Multiple accounts in import, will check all accounts');
        }
      }
      
      const hasExisting = await hasExistingTransactions(userId, targetAccountForDuplicateCheck);
      console.log('🔎 Duplicate check decision:', { hasExisting, accountId, targetAccountForDuplicateCheck, userId, skipDuplicateCheck, mode: duplicateCheckMode });
      
      let isOptimizedImport = false;
      
      // Check if user wants to skip duplicate checking
      if (skipDuplicateCheck) {
        console.log('⏭️ SKIPPING: User chose to skip duplicate checking');
        toast.loading('Skipping duplicate check (user preference)...', { id: 'import-progress' });
        
        duplicateCheckResults = finalTransactions.map((transaction) => ({
          isDuplicate: false,
          newTransaction: transaction,
          potentialDuplicates: []
        }));
        
        isOptimizedImport = true;
        setProgress(65);
      } else if (hasExisting) {
        console.log(`⏳ Running ${duplicateCheckMode} duplicate check - existing transactions found`);
        
        // Show appropriate loading message based on mode and batch size
        const batchSize = finalTransactions.length;
        let loadingMessage = 'Checking for duplicates...';
        
        if (duplicateCheckMode === DuplicateCheckMode.FAST && batchSize >= 100) {
          loadingMessage = `🚀 Fast duplicate check (server-side) for ${batchSize} transactions...`;
        } else if (batchSize >= 1000) {
          loadingMessage = `⚡ Processing ${batchSize} transactions (this may take a moment)...`;
        }
        
        toast.loading(loadingMessage, { id: 'import-progress' });
        
        try {
          // Use the smart duplicate checking that chooses the best method
          duplicateCheckResults = await checkBatchForDuplicatesSmart(
            userId, 
            finalTransactions, 
            duplicateCheckMode,
            duplicateCheckMode === DuplicateCheckMode.FAST ? FAST_DUPLICATE_CRITERIA : DEFAULT_DUPLICATE_CRITERIA
          );
          
          setDuplicateResults(duplicateCheckResults);
          setProgress(65);
          
          const duplicateCount = duplicateCheckResults.filter(r => r.isDuplicate).length;
          console.log(`📊 Duplicate check complete: ${duplicateCount} duplicates found out of ${finalTransactions.length} transactions`);
          
        } catch (error) {
          console.error('Error checking for duplicates:', error);
          // Continue with import if duplicate check fails
          toast.warning('Duplicate check failed, continuing with import...', { duration: 3000 });
          
          duplicateCheckResults = finalTransactions.map((transaction) => ({
            isDuplicate: false,
            newTransaction: transaction,
            potentialDuplicates: []
          }));
        }
      } else {
        console.log('⚡ OPTIMIZATION: Skipping duplicate check - no existing transactions found');
        isOptimizedImport = true;
        
        toast.loading('Optimizing import (skipping duplicate check for fresh account)...', { id: 'import-progress' });
        
        duplicateCheckResults = finalTransactions.map((transaction) => ({
          isDuplicate: false,
          newTransaction: transaction,
          potentialDuplicates: []
        }));
        
        setProgress(65);
      }
      
      console.log(`✅ STEP 5 Complete: Duplicate check finished, ${duplicateCheckResults.filter(r => r.isDuplicate).length} duplicates found`);
      
      // STEP 6: Separate transactions and handle duplicates (65-70% progress)
      console.log('🚀 STEP 6: Handling duplicates...');
      
      const transactionsWithDuplicates: any[] = [];
      const transactionsWithoutDuplicates: any[] = [];
      
      duplicateCheckResults.forEach((result, index) => {
        if (result.isDuplicate) {
          transactionsWithDuplicates.push({
            transaction: finalTransactions[index],
            duplicateResult: result
          });
        } else {
          transactionsWithoutDuplicates.push(finalTransactions[index]);
        }
      });
      
      // If there are duplicates, ask user what to do
      if (transactionsWithDuplicates.length > 0) {
        setPendingTransactions(transactionsWithoutDuplicates);
        setShowDuplicatePrompt(true);
        setImporting(false);
        toast.dismiss('import-progress');
        
        toast.warning(
          `Found ${transactionsWithDuplicates.length} potential duplicate transactions. Please review and decide how to proceed.`,
          { duration: 5000 }
        );
        return { imported: [], skipped: [], total: finalTransactions.length };
      }
      
      setProgress(70);
      console.log(`✅ STEP 6 Complete: ${transactionsWithoutDuplicates.length} transactions ready for bulk import`);
      
      // STEP 7: BULK INSERT ALL TRANSACTIONS (70-100% progress)
      console.log('🚀 STEP 7: Bulk inserting transactions...');
      toast.loading('Bulk importing transactions...', { id: 'import-progress' });
      
      try {
        // Clean up temporary properties before bulk insert
        const cleanTransactions = transactionsWithoutDuplicates.map(tx => {
          const clean = { ...tx };
          delete clean.originalCategory;
          delete clean._rowIndex;
          return clean;
        });
        
        console.log(`Bulk inserting ${cleanTransactions.length} transactions...`);
        
        // Use the transaction service bulk insert method
        const insertedTransactions = await transactionService.bulkInsertTransactions(userId, cleanTransactions);
        
        console.log(`✅ Successfully bulk inserted ${insertedTransactions.length} transactions`);
        
        // Add all transactions to imported list
        imported.push(...transactionsWithoutDuplicates);
        setImportedCount(imported.length);
        
        setProgress(100);
        
        // Invalidate queries to refresh the transactions list
        invalidateTransactions(queryClient);
        
        toast.dismiss('import-progress');
        
        // Generate the report URL
        const reportUrl = generateCSVReport();
        
        // Show success message with download option
        toast.success(
          <div className="flex flex-col gap-2">
            <div>
              Successfully imported {imported.length} of {total} transactions.
              {skipped.length > 0 && (
                <span className="block text-sm text-gray-500">
                  {skipped.length} transactions were skipped.
                </span>
              )}
              {isOptimizedImport && (
                <span className="block text-sm text-green-600">
                  ⚡ Optimized import - duplicate check skipped for fresh account
                </span>
              )}
            </div>
            {reportUrl && (
              <a 
                href={reportUrl} 
                download="import-report.csv"
                className="flex items-center gap-1 text-sm font-medium text-[#439476] hover:underline"
              >
                <Download size={14} /> Download report
              </a>
            )}
          </div>,
          {
            duration: 10000,
          }
        );

        // Show summary of invalid rule categories if any were found
        if (invalidRuleCategoryIds.size > 0) {
          console.warn(
            `🔧 AUTOMATION RULES NEED ATTENTION: ${invalidRuleCategoryIds.size} rules reference deleted categories:`,
            Array.from(invalidRuleCategoryIds)
          );
          
          toast.warning(
            <div className="flex flex-col gap-2">
              <div className="font-medium">Automation Rules Need Attention</div>
              <div className="text-sm">
                {invalidRuleCategoryIds.size} rules reference deleted categories. 
                Please update your automation rules to fix category assignments.
              </div>
            </div>,
            {
              duration: 8000,
            }
          );
        }
        
      } catch (error) {
        console.error('Error in bulk transaction insert:', error);
        
        // Fallback to individual inserts if bulk insert fails
        console.log('Bulk insert failed, falling back to individual inserts...');
        toast.loading('Bulk import failed, processing individually...', { id: 'import-progress' });
        
        // Process in smaller batches as fallback
        let processedCount = 0;
        const batchSize = 10;
        
        for (let i = 0; i < transactionsWithoutDuplicates.length; i += batchSize) {
          const batch = transactionsWithoutDuplicates.slice(i, Math.min(i + batchSize, transactionsWithoutDuplicates.length));
          
          const promises = batch.map(async (finalTransaction) => {
            try {
              // Remove temp properties before saving
              const cleanTransaction = { ...finalTransaction };
              delete cleanTransaction.originalCategory;
              delete cleanTransaction._rowIndex;
              
              // Add transaction to database
              await coreTransactionStore.addTransaction(userId, cleanTransaction);
              
              // Add to imported list
              imported.push(finalTransaction);
              
              // Update imported count
              setImportedCount(prev => prev + 1);
              
              return true;
            } catch (error) {
              console.error(`Error adding transaction from row ${finalTransaction._rowIndex + 1}:`, error);
              const originalRowIndex = (finalTransaction as any)._rowIndex;
              skipped.push({
                data: originalRowIndex !== undefined ? dataMap.get(originalRowIndex) : finalTransaction,
                reason: error instanceof Error ? error.message : 'Error adding transaction'
              });
              return false;
            }
          });
          
          await Promise.all(promises);
          processedCount += batch.length;
          
          // Update progress (70-100%)
          const currentProgress = 70 + Math.round((processedCount / transactionsWithoutDuplicates.length) * 30);
          setProgress(Math.min(currentProgress, 100));
        }
        
        // Invalidate queries to refresh the transactions list
        invalidateTransactions(queryClient);
        
        // Update progress for the last time
        setProgress(100);
        toast.dismiss('import-progress');
        
        // Generate the report URL
        const reportUrl = generateCSVReport();
        
        // Show success message with download option
        toast.success(
          <div className="flex flex-col gap-2">
            <div>
              Successfully imported {imported.length} of {total} transactions.
              {skipped.length > 0 && (
                <span className="block text-sm text-gray-500">
                  {skipped.length} transactions were skipped.
                </span>
              )}
              <span className="block text-sm text-blue-600">
                ℹ️ Used fallback method - bulk import failed but individual processing succeeded
              </span>
            </div>
            {reportUrl && (
              <a 
                href={reportUrl} 
                download="import-report.csv"
                className="flex items-center gap-1 text-sm font-medium text-[#439476] hover:underline"
              >
                <Download size={14} /> Download report
              </a>
            )}
          </div>,
          {
            duration: 10000,
          }
        );
      }
      
      console.log(`✅ STEP 7 Complete: Import finished with ${imported.length} imported, ${skipped.length} skipped`);
      
      // Log unmatched categories for debugging
      const unmatchedCategoriesList = Array.from(unmatchedCategories);
      if (unmatchedCategoriesList.length > 0) {
        console.log('Unmatched categories:', unmatchedCategoriesList);
      }
    } catch (error) {
      console.error('Error in transaction processing:', error);
      toast.dismiss('import-progress');
      throw error;
    }

    setErrors(newErrors);
    setUnmatchedCategories(localUnmatchedCategories);

    return { imported, skipped, total };
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      handleFileSelect(acceptedFiles[0]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.csv'],
      'application/pdf': ['.pdf']
    },
    maxFiles: 1
  });

  // Helper function to format file size
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' bytes';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Function to parse date based on selected format
  const parseDate = (dateStr: string, format: DateFormat): Date => {
    try {
      if (!dateStr) {
        throw new Error('Date field is empty');
      }

      // Handle ISO format with timezone
      if (format.id === 'YYYY-MM-DDTHH:mm:ssZ') {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) {
          throw new Error(`Invalid ISO date format: ${dateStr}`);
        }
        return date;
      }

      // Handle ISO format without timezone
      if (format.id === 'YYYY-MM-DDTHH:mm:ss') {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) {
          throw new Error(`Invalid ISO date format: ${dateStr}`);
        }
        return date;
      }

      // Split date and time if present
      const [datePart, timePart] = dateStr.split(' ');
      if (!datePart) {
        throw new Error(`Invalid date format. Expected: ${format.format}, Got: ${dateStr}`);
      }

      // Parse the date part
      let year: number, month: number, day: number;
      let separator = '/';
      if (datePart.includes('/')) {
        separator = '/';
      } else if (datePart.includes('-')) {
        separator = '-';
      } else if (datePart.includes('.')) {
        separator = '.';
      }
      const dateParts = datePart.split(separator);

      if (dateParts.length !== 3) {
        throw new Error(`Date must have 3 parts. Got: ${datePart}`);
      }

      const numbers = dateParts.map(Number);
      if (numbers.some(isNaN)) {
        throw new Error(`Date parts must be numbers. Got: ${datePart}`);
      }

      // Handle different date part orders based on format
      if (format.id.startsWith('YYYY')) {
        [year, month, day] = numbers;
      } else if (format.id.startsWith('MM')) {
        [month, day, year] = numbers;
      } else {
        [day, month, year] = numbers;
      }

      // Handle 2-digit year
      if (year < 100) {
        year += year < 50 ? 2000 : 1900;
      }

      // Validate month
      if (month < 1 || month > 12) {
        throw new Error(`Invalid month: ${month}. Month must be between 1 and 12`);
      }

      // Validate day
      const daysInMonth = new Date(year, month, 0).getDate();
      if (day < 1 || day > daysInMonth) {
        throw new Error(`Invalid day: ${day}. Day must be between 1 and ${daysInMonth} for month ${month}`);
      }

      // Parse time if present
      let hours = 0, minutes = 0, seconds = 0;
      if (timePart) {
        // Handle AM/PM format
        const isAMPM = format.format.includes('A');
        const timeStr = timePart.replace(/\s?[AaPp][Mm]$/, ''); // Remove AM/PM
        const isPM = /[Pp][Mm]$/.test(timePart);

        const timeParts = timeStr.split(':');
        if (timeParts.length < 2) {
          throw new Error(`Invalid time format. Expected HH:mm or hh:mm AM/PM, Got: ${timePart}`);
        }

        [hours, minutes] = timeParts.map(Number);
        if (timeParts.length > 2) {
          seconds = Number(timeParts[2]);
        }

        if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) {
          throw new Error(`Time parts must be numbers. Got: ${timePart}`);
        }

        // Handle 12-hour format
        if (isAMPM) {
          if (hours < 1 || hours > 12) {
            throw new Error(`Invalid hours: ${hours}. Hours must be between 1 and 12 for 12-hour format`);
          }
          if (isPM && hours !== 12) hours += 12;
          if (!isPM && hours === 12) hours = 0;
        } else {
          // 24-hour format validation
          if (hours < 0 || hours > 23) {
            throw new Error(`Invalid hours: ${hours}. Hours must be between 0 and 23 for 24-hour format`);
          }
        }

        if (minutes < 0 || minutes > 59) {
          throw new Error(`Invalid minutes: ${minutes}. Minutes must be between 0 and 59`);
        }
        if (seconds < 0 || seconds > 59) {
          throw new Error(`Invalid seconds: ${seconds}. Seconds must be between 0 and 59`);
        }
      }

      const date = new Date(year, month - 1, day, hours, minutes, seconds);

      // Final validation
      if (isNaN(date.getTime())) {
        throw new Error(`Invalid date combination: ${dateStr}`);
      }

      return date;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Row date error: ${error.message}`);
      }
      throw new Error(`Invalid date format: ${dateStr}. Expected format: ${format.format}`);
    }
  };

  const handleFileSelect = async (file: File) => {
    try {
      if (!file) {
        toast.error("No file selected");
        return;
      }

      if (accountId && !accounts.find(a => a.id === accountId)) {
        toast.error("Selected account not found");
        return;
      }

      setSelectedFile(file);

      // PDF bank statements go to the server-side deterministic parser and
      // come back as CSV-shaped rows; everything downstream is unchanged.
      if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
        setParsingStatement(true);
        setErrors([]);
        try {
          const buffer = await file.arrayBuffer();
          const bytes = new Uint8Array(buffer);
          let binary = '';
          const CHUNK = 0x8000;
          for (let i = 0; i < bytes.length; i += CHUNK) {
            binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
          }
          const { data, error } = await parseStatementPdf(btoa(binary));
          if (error) {
            toast.error(STATEMENT_ERROR_MESSAGES[error.error]);
            setSelectedFile(null);
            return;
          }
          setStatementResult(data);
          setParsedData(mapStatementRowsToCsvShape(data));
          // Parser emits ISO dates; no detection needed.
          setSelectedDateFormat('YYYY-MM-DD');
          setDetectedDateFormat('YYYY-MM-DD');
          if (data.reconciliation.ok) {
            toast.success(
              `Statement parsed: ${data.reconciliation.rowCount} transactions, balanced against the statement totals.`
            );
          } else {
            toast.warning(
              'Statement parsed, but the totals do not balance — review before importing.'
            );
          }
        } catch (err) {
          console.error('Statement parse failed:', err);
          toast.error(STATEMENT_ERROR_MESSAGES.statement_unavailable);
          setSelectedFile(null);
        } finally {
          setParsingStatement(false);
        }
        return;
      }

      parse(file, {
        header: true,
        skipEmptyLines: true,
        delimiter: '', // Auto-detect delimiter (comma, semicolon, tab)
        complete: (results) => {
          console.log('CSV parsing complete:', results);
          setParsedData(results.data);
          setErrors([]);
          
          // Auto-detect date format
          const detectedFormat = detectDateFormat(results.data);
          if (detectedFormat) {
            setDetectedDateFormat(detectedFormat);
            setSelectedDateFormat(detectedFormat);
            toast.success(
              `Date format auto-detected: ${DATE_FORMATS.find(f => f.id === detectedFormat)?.label}`,
              { duration: 3000 }
            );
          } else {
            setDetectedDateFormat(null);
            toast.info("Could not auto-detect date format. Please select manually.", { duration: 3000 });
          }
        },
        error: (error) => {
          console.error('Error parsing CSV:', error);
          toast.error("Failed to parse CSV file");
          setErrors([error.message]);
          setSelectedFile(null);
          setParsedData(null);
          setDetectedDateFormat(null);
        }
      });
    } catch (error) {
      console.error('Error reading file:', error);
      toast.error("Failed to read file");
      if (error instanceof Error) {
        setErrors([error.message]);
      }
      setDetectedDateFormat(null);
    }
  };

  // The account rows will land in, and whether it disagrees with the
  // statement's currency. Transactions take the account's currency, so a
  // mismatch would silently record TRY amounts as USD (or vice versa).
  const effectiveAccount = useMemo(
    () => accounts.find((a: Account) => a.id === (accountId || selectedAccountId)),
    [accounts, accountId, selectedAccountId]
  );
  const currencyMismatch = Boolean(
    statementResult &&
      effectiveAccount?.currency &&
      effectiveAccount.currency !== statementResult.meta.currency
  );

  // Category options for the review table, in the "Parent - Child" naming
  // that findBestMatchingCategory resolves. Handles both store shapes
  // (nested subcategories or flat with parent_name).
  const categoryItems = useMemo(() => {
    const items: { value: string; label: string }[] = [];
    for (const cat of (categories ?? []) as Category[]) {
      if (cat.parent_id || cat.parent_name) continue; // children handled via parent
      items.push({ value: cat.name, label: cat.name });
      for (const sub of cat.subcategories ?? []) {
        const full = `${cat.name} - ${sub.name}`;
        items.push({ value: full, label: full });
      }
    }
    for (const cat of (categories ?? []) as Category[]) {
      if (!cat.parent_name || cat.subcategories?.length) continue;
      const full = `${cat.parent_name} - ${cat.name}`;
      items.push({ value: full, label: full });
    }
    return items;
  }, [categories]);

  // Persist review-screen category corrections onto the payees table so the
  // server-side history layer auto-applies them on future statement imports.
  // Runs after processTransactions, which is when the payees exist.
  const persistRememberedCategories = async () => {
    if (!rememberCorrections || rememberedCategories.size === 0 || !user?.id) return;
    try {
      const entries = [...rememberedCategories.values()];
      const { data: payeeRows } = await supabase
        .from('payees')
        .select('id,name')
        .eq('user_id', user.id)
        .in('name', entries.map((e) => e.payee));
      if (!payeeRows?.length) return;
      await Promise.all(
        payeeRows.map((p: { id: string; name: string }) => {
          const remembered = rememberedCategories.get(p.name.trim().toLowerCase());
          const category = remembered ? findBestMatchingCategory(remembered.categoryName) : null;
          if (!category) return Promise.resolve(null);
          return supabase.from('payees').update({ category_id: category.id }).eq('id', p.id);
        })
      );
    } catch (err) {
      // Remembering is best-effort — never let it affect the import itself.
      console.error('Failed to persist payee category corrections:', err);
    }
  };

  const handleImport = async () => {
    if (!parsedData || !user?.id) {
      toast.error('No data to import or user not authenticated');
      return;
    }
    
    setImporting(true);
    setProgress(0);
    setErrors([]);
    setUnmatchedCategories(new Set());
    setInvalidRuleCategoryIds(new Set());
    setImportCompleted(false);
    
    const loadingToast = toast.loading('Importing transactions...', {
      description: 'Processing data locally. This may take a moment.',
      duration: 10000,
    });
    
    try {
      // Process the transactions
      const results = await processTransactions(parsedData, user.id);

      // Payees now exist — persist any remembered category corrections.
      await persistRememberedCategories();

      // Store the results for reporting
      setImportResults(results);
      
      // Generate the report URL
      const reportUrl = generateCSVReport();
      
      // Mark import as completed to update UI
      setImportCompleted(true);
      
      // Show success message with download option
      toast.success(
        <div className="flex flex-col gap-2">
          <div>
            Successfully imported {results.imported.length} of {results.total} transactions.
            {results.skipped.length > 0 && (
              <span className="block text-sm text-gray-500">
                {results.skipped.length} transactions were skipped.
              </span>
            )}
          </div>
          {reportUrl && (
            <a 
              href={reportUrl} 
              download="import-report.csv"
              className="flex items-center gap-1 text-sm font-medium text-[#439476] hover:underline"
            >
              <Download size={14} /> Download report
            </a>
          )}
        </div>,
        {
          duration: 10000,
        }
      );
      
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Error importing transactions', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      toast.dismiss(loadingToast);
      setImporting(false);
    }
  };

  const generateCSVReport = () => {
    if (!importResults.total) return null;
    
    // Get the list of unmatched categories for the report
    const unmatchedCategoriesList = Array.from(unmatchedCategories);
    
    const reportData = [
      ...importResults.imported.map(transaction => {
        // Find the category name if category_id exists
        let categoryName = '';
        if (transaction.category_id) {
          // Find the category name if category_id exists
          const category = categories.find(c => c.id === transaction.category_id);
          if (category) {
            // Check if this is a subcategory (has parent_id property)
            const categoryWithParent = category as any;
            if (categoryWithParent.parent_id) {
              // This is a subcategory, find the parent
              const parentCategory = categories.find(c => c.id === categoryWithParent.parent_id);
              if (parentCategory) {
                categoryName = `${parentCategory.name} - ${category.name}`;
              } else {
                // Fallback if parent not found
                categoryName = category.name;
              }
            } else if (categoryWithParent.parent_name) {
              // Check if this category has a parent_name property (alternative structure)
              categoryName = `${categoryWithParent.parent_name} - ${category.name}`;
            } else {
              // It's a main category
              categoryName = category.name;
            }
          }
        }
        
        // Add the original category from CSV if it exists and wasn't matched
        const originalCategoryInfo = transaction.originalCategory && !transaction.category_id
          ? ` (Original: ${transaction.originalCategory})`
          : '';
        
        return {
          Date: transaction.date ? new Date(transaction.date).toLocaleDateString() : '',
          Description: transaction.description || '',
          Category: categoryName + originalCategoryInfo,
          Amount: transaction.amount || '',
          Type: transaction.type || '',
          Account: accounts.find(a => a.id === transaction.account_id)?.name || '',
          Payee: transaction.payee_name || '',
          Tags: Array.isArray(transaction.tags) ? transaction.tags.join(', ') : '',
          Notes: transaction.notes || '',
          Status: 'Imported',
          Reason: transaction.originalCategory && !transaction.category_id 
            ? `Category not matched: ${transaction.originalCategory}` 
            : ''
        };
      }),
      ...importResults.skipped.map(skippedTransaction => {
        // Ensure we have the original CSV data for skipped transactions
        const data = skippedTransaction.data || {};
        return {
          Date: data.Date || '',
          Description: data.Description || '',
          Category: data.Category || '',
          Amount: data.Amount || '',
          Type: data.Type || '',
          Account: data.Account || '',
          Payee: data.Payee || '',
          Tags: data.Tags || '',
          Notes: data.Notes || '',
          Status: 'Skipped',
          Reason: skippedTransaction.reason || 'Unknown error'
        };
      })
    ];
    
    // Add a summary row with unmatched categories if any exist
    if (unmatchedCategoriesList.length > 0) {
      reportData.push({
        Date: '',
        Description: '',
        Category: '',
        Amount: '',
        Type: '',
        Account: '',
        Payee: '',
        Tags: '',
        Notes: '',
        Status: 'Summary',
        Reason: `Unmatched categories: ${unmatchedCategoriesList.join(', ')}`
      });
    }
    
    const csv = unparse(reportData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    
    return url;
  };

  const generateCSVTemplate = () => {
    // Get the first account name for the example or use a placeholder
    const accountName = accounts.length > 0 ? accounts[0].name : 'My Account';
    
    // Create example data with all possible fields
    const exampleData = [
      {
        Date: new Date().toLocaleDateString('en-GB'), // Use current date in DD/MM/YYYY format
        Description: 'Grocery shopping',
        Category: 'Food & Dining - Groceries',
        Amount: '-45.67',
        Type: 'expense',
        Account: accountName,
        Payee: 'Local Supermarket',
        Tags: 'groceries,food,essentials',
        Notes: 'Weekly grocery shopping'
      },
      {
        Date: new Date().toLocaleDateString('en-GB'),
        Description: 'Salary payment',
        Category: 'Income - Salary',
        Amount: '2500.00',
        Type: 'income',
        Account: accountName,
        Payee: 'Employer Inc.',
        Tags: 'income,salary',
        Notes: 'Monthly salary'
      },
      {
        Date: new Date().toLocaleDateString('en-GB'),
        Description: 'Restaurant dinner',
        Category: 'Food & Dining - Restaurants',
        Amount: '-78.50',
        Type: 'expense',
        Account: accountName,
        Payee: 'Nice Restaurant',
        Tags: 'dining,entertainment',
        Notes: 'Dinner with friends'
      }
    ];
    
    // Convert to CSV
    const csv = unparse(exampleData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    
    return url;
  };

  // Generate template URL
  const templateUrl = generateCSVTemplate();

  // Generate CSV report URL
  const reportUrl = importResults.total > 0 ? generateCSVReport() : null;

  // Handle duplicate confirmation - import all transactions including duplicates
  const handleDuplicateConfirm = async () => {
    setShowDuplicatePrompt(false);
    setImporting(true);
    
    try {
      const allTransactions = [...pendingTransactions];
      
      // Add transactions with duplicates too
      duplicateResults.forEach((result) => {
        if (result.isDuplicate) {
          allTransactions.push(result.newTransaction);
        }
      });
      
      toast.loading('Importing all transactions...', { id: 'import-progress' });
      
      try {
        // Clean up temporary properties before bulk insert
        const cleanTransactions = allTransactions.map(tx => {
          const clean = { ...tx };
          delete clean.originalCategory;
          delete clean._rowIndex;
          return clean;
        });
        
        console.log(`Bulk inserting ${cleanTransactions.length} transactions (including duplicates)...`);
        
        // Use bulk insert for better performance
        const insertedTransactions = await transactionService.bulkInsertTransactions(user!.id, cleanTransactions);
        
        toast.dismiss('import-progress');
        invalidateTransactions(queryClient);
        setImportResults({ 
          imported: allTransactions, 
          skipped: [], 
          total: allTransactions.length 
        });
        setImportCompleted(true);
        setImportedCount(allTransactions.length);
        
        toast.success(`Successfully bulk imported ${insertedTransactions.length} transactions (including duplicates)`);
        
      } catch (bulkError) {
        console.error('Bulk import failed, falling back to individual processing:', bulkError);
        
        // Fallback to individual processing
        const imported: any[] = [];
        const skipped: { data: any; reason: string }[] = [];
        
        for (let i = 0; i < allTransactions.length; i++) {
          try {
            const transaction = allTransactions[i];
            const cleanTransaction = { ...transaction };
            delete cleanTransaction.originalCategory;
            delete cleanTransaction._rowIndex;
            
            await coreTransactionStore.addTransaction(user!.id, cleanTransaction);
            imported.push(transaction);
            setImportedCount(prev => prev + 1);
            
            setProgress(Math.round(((i + 1) / allTransactions.length) * 100));
          } catch (error) {
            console.error('Error importing transaction:', error);
            const transaction = allTransactions[i];
            const originalRowIndex = (transaction as any)._rowIndex;
            skipped.push({
              data: originalRowIndex !== undefined ? originalDataMap.get(originalRowIndex) : transaction,
              reason: error instanceof Error ? error.message : 'Failed to import'
            });
          }
        }
        
        toast.dismiss('import-progress');
        invalidateTransactions(queryClient);
        setImportResults({ imported, skipped, total: allTransactions.length });
        setImportCompleted(true);
        
        toast.success(`Imported ${imported.length} transactions (including duplicates)`);
      }
    } catch (error) {
      console.error('Error in duplicate confirmation:', error);
      toast.error('Error importing transactions');
    } finally {
      setImporting(false);
    }
  };

  // Handle skip duplicates - import only non-duplicate transactions
  const handleSkipDuplicates = async () => {
    setShowDuplicatePrompt(false);
    setImporting(true);
    
    try {
      // Add transactions marked as duplicates to skipped list
      const skipped: { data: any; reason: string }[] = [];
      duplicateResults.forEach((result) => {
        if (result.isDuplicate) {
          const rowIndex = (result.newTransaction as any)._rowIndex;
          skipped.push({
            data: rowIndex !== undefined ? originalDataMap.get(rowIndex) : result.newTransaction,
            reason: 'Skipped due to potential duplicate'
          });
        }
      });
      
      toast.loading('Importing non-duplicate transactions...', { id: 'import-progress' });
      
      try {
        // Clean up temporary properties before bulk insert
        const cleanTransactions = pendingTransactions.map(tx => {
          const clean = { ...tx };
          delete clean.originalCategory;
          delete clean._rowIndex;
          return clean;
        });
        
        console.log(`Bulk inserting ${cleanTransactions.length} non-duplicate transactions...`);
        
        // Use bulk insert for better performance
        const insertedTransactions = await transactionService.bulkInsertTransactions(user!.id, cleanTransactions);
        
        toast.dismiss('import-progress');
        invalidateTransactions(queryClient);
        setImportResults({ 
          imported: pendingTransactions, 
          skipped, 
          total: pendingTransactions.length + duplicateResults.filter(r => r.isDuplicate).length 
        });
        setImportCompleted(true);
        setImportedCount(pendingTransactions.length);
        
        toast.success(`Successfully bulk imported ${insertedTransactions.length} transactions (skipped ${duplicateResults.filter(r => r.isDuplicate).length} duplicates)`);
        
      } catch (bulkError) {
        console.error('Bulk import failed, falling back to individual processing:', bulkError);
        
        // Fallback to individual processing
        const imported: any[] = [];
        
        for (let i = 0; i < pendingTransactions.length; i++) {
          try {
            const transaction = pendingTransactions[i];
            const cleanTransaction = { ...transaction };
            delete cleanTransaction.originalCategory;
            delete cleanTransaction._rowIndex;
            
            await coreTransactionStore.addTransaction(user!.id, cleanTransaction);
            imported.push(transaction);
            setImportedCount(prev => prev + 1);
            
            setProgress(Math.round(((i + 1) / pendingTransactions.length) * 100));
          } catch (error) {
            console.error('Error importing transaction:', error);
            const transaction = pendingTransactions[i];
            const originalRowIndex = (transaction as any)._rowIndex;
            skipped.push({
              data: originalRowIndex !== undefined ? originalDataMap.get(originalRowIndex) : transaction,
              reason: error instanceof Error ? error.message : 'Failed to import'
            });
          }
        }
        
        toast.dismiss('import-progress');
        invalidateTransactions(queryClient);
        setImportResults({ imported, skipped, total: pendingTransactions.length + duplicateResults.filter(r => r.isDuplicate).length });
        setImportCompleted(true);
        
        toast.success(`Imported ${imported.length} transactions (skipped ${duplicateResults.filter(r => r.isDuplicate).length} duplicates)`);
      }
    } catch (error) {
      console.error('Error in skip duplicates:', error);
      toast.error('Error importing transactions');
    } finally {
      setImporting(false);
    }
  };

  // Helper function to check if account has existing transactions
  const hasExistingTransactions = async (userId: string, targetAccountId?: string): Promise<boolean> => {
    try {
      console.log('🔍 Checking for existing transactions:', { userId, targetAccountId });
      
      let query = supabase
        .from('transactions')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId);
      
      // If importing to a specific account, check only that account
      if (targetAccountId) {
        // Get account name for better logging
        const account = accounts.find(a => a.id === targetAccountId);
        const accountName = account?.name || 'Unknown Account';
        console.log('🎯 Checking specific account:', { accountId: targetAccountId, accountName });
        query = query.eq('account_id', targetAccountId);
      } else {
        console.log('🌐 Checking all accounts for user');
      }
      
      const { count, error } = await query;
      
      if (error) {
        console.error('❌ Error checking existing transactions:', error);
        return true; // Assume there are transactions to be safe
      }
      
      const hasTransactions = (count || 0) > 0;
      
      if (targetAccountId) {
        const account = accounts.find(a => a.id === targetAccountId);
        const accountName = account?.name || 'Unknown Account';
        console.log(`📊 Account "${accountName}" has ${count} existing transactions:`, { count, hasTransactions });
      } else {
        console.log('📊 Total transactions across all accounts:', { count, hasTransactions });
      }
      
      return hasTransactions;
    } catch (error) {
      console.error('❌ Exception checking existing transactions:', error);
      return true; // Assume there are transactions to be safe
    }
  };

  return (
    <StyledModal
      title={importCompleted ? "Import Results" : "Import Transactions"}
      isOpen={true}
      onClose={onClose}
      maxWidth={statementResult && parsedData && !importCompleted ? "1080px" : "650px"}
      className="overflow-visible"
      footer={
        !importCompleted ? (
          <>
            <StyledModalButton
              onClick={handleImport}
              disabled={
                !parsedData ||
                importing ||
                parsingStatement ||
                // Statement imports: the reconciliation gate must be green
                // (or explicitly overridden) and an account chosen.
                (!!statementResult &&
                  ((!statementResult.reconciliation.ok && !reconciliationOverride) ||
                    (!accountId && !selectedAccountId))) ||
                // A currency mismatch would silently mislabel every amount.
                (currencyMismatch && !currencyAck)
              }
              fullWidth
              variant="primary"
            >
              {importing
                ? 'Importing...'
                : statementResult && !accountId && !selectedAccountId
                ? 'Select an account to import'
                : 'Import Transactions'}
            </StyledModalButton>
            <StyledModalButton
              onClick={onClose}
              variant="outline"
            >
              Cancel
            </StyledModalButton>
          </>
        ) : null
      }
    >
      {importing && (
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm text-[#2F2F2F] dark:text-[#A3A3A3]">
              Importing transactions... ({importedCount}/{parsedData?.length || 0})
            </span>
            <span className="text-sm font-medium text-[#439476]">{progress}%</span>
          </div>
          <Progress value={progress} className="h-2 bg-[#E5E7EB] dark:bg-[#1E2B42] [&>div]:bg-gradient-to-r [&>div]:from-[#439476] [&>div]:to-[rgba(8,31,61,1)]" />
          <div className="flex justify-between text-xs text-[#2F2F2F] dark:text-[#A3A3A3] mt-1">
            <span>
              {progress < 30 ? 'Validating' : 
               progress < 40 ? 'Processing tags' : 
               progress < 50 ? 'Processing payees' : 
               progress < 60 ? 'Preparing transactions' :
               progress < 70 ? 'Checking for duplicates' :
               'Adding transactions'}
            </span>
            <span>{importedCount} imported</span>
          </div>
        </div>
      )}
      
      {/* Duplicate Detection Prompt */}
      {showDuplicatePrompt && (
        <div className="space-y-4">
          <StyledModalSection 
            title="Duplicate Transactions Detected" 
            primaryColor="#f97316"
          >
            <div className="flex items-center gap-3 p-4 bg-orange-100 dark:bg-orange-900/30 rounded-xl border border-orange-200 dark:border-orange-800">
              <AlertTriangle className="w-6 h-6 text-orange-600" />
              <div>
                <h3 className="font-semibold text-orange-800 dark:text-orange-200">
                  Potential Duplicates Found
                </h3>
                <p className="text-sm text-orange-700 dark:text-orange-300">
                  Found {duplicateResults.filter(r => r.isDuplicate).length} potentially duplicate transactions out of {duplicateResults.length} total.
                </p>
              </div>
            </div>
            
            <div className="mt-4 space-y-3">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowDuplicatesInReport(!showDuplicatesInReport)}
                  className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  {showDuplicatesInReport ? <EyeOff size={16} /> : <Eye size={16} />}
                  {showDuplicatesInReport ? 'Hide' : 'Show'} duplicate details
                </button>
              </div>
              
              {showDuplicatesInReport && (
                <div className="max-h-40 overflow-y-auto space-y-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  {duplicateResults
                    .filter(r => r.isDuplicate)
                    .slice(0, 5) // Show only first 5 duplicates
                    .map((result, index) => (
                      <div key={index} className="text-xs p-2 bg-white dark:bg-gray-700 rounded border">
                        <div className="font-medium">
                          {result.newTransaction.description || 'No description'}
                        </div>
                        <div className="text-gray-500">
                          Amount: {result.newTransaction.amount} • 
                          Date: {new Date(result.newTransaction.date!).toLocaleDateString()}
                        </div>
                        <div className="text-orange-600 mt-1">
                          Matches: {result.potentialDuplicates[0]?.matchReasons.join(', ')}
                        </div>
                      </div>
                    ))}
                  {duplicateResults.filter(r => r.isDuplicate).length > 5 && (
                    <div className="text-xs text-gray-500 text-center p-2">
                      ... and {duplicateResults.filter(r => r.isDuplicate).length - 5} more
                    </div>
                  )}
                </div>
              )}
            </div>
          </StyledModalSection>
          
          <div className="flex gap-3">
            <StyledModalButton
              onClick={handleSkipDuplicates}
              variant="secondary"
              fullWidth
            >
              Skip Duplicates ({duplicateResults.filter(r => r.isDuplicate).length})
            </StyledModalButton>
            <StyledModalButton
              onClick={handleDuplicateConfirm}
              variant="primary"
              fullWidth
            >
              Import All ({duplicateResults.length})
            </StyledModalButton>
          </div>
          
          <StyledModalButton
            onClick={() => {
              setShowDuplicatePrompt(false);
              setImporting(false);
              setPendingTransactions([]);
              setDuplicateResults([]);
            }}
            variant="outline"
            fullWidth
          >
            Cancel Import
          </StyledModalButton>
        </div>
      )}
      
      {!importCompleted && !showDuplicatePrompt && (
        <>
          {!selectedFile ? (
            <div className="mb-6">
              <div 
                {...getRootProps()} 
                className={`flex flex-col items-center justify-center p-8 border-2 border-dashed rounded-xl transition-colors
                  ${isDragActive 
                    ? 'border-[rgba(8,31,61,1)] bg-[rgba(8,31,61,0.1)]' 
                    : 'border-[#2F2F2F]/10 dark:border-[#333840] hover:bg-[#F3F4F6] dark:hover:bg-[#1E2B42]'
                  }`}
              >
                <input {...getInputProps()} />
                <Upload className={`w-12 h-12 mb-4 ${isDragActive ? 'text-[rgba(8,31,61,1)]' : 'text-[#439476]'}`} />
                <p className="text-center text-[#2F2F2F] dark:text-[#A3A3A3]">
                  {isDragActive
                    ? "Drop the file here"
                    : "Drag and drop a CSV file or a bank statement PDF here, or click to select"}
                </p>
                <p className="text-center text-xs mt-2 text-[#2F2F2F]/70 dark:text-[#A3A3A3]/70">
                  Statement PDFs are processed in memory and never stored.
                </p>
              </div>
            </div>
          ) : (
            <div className="mb-6">
              <div className="flex items-start p-4 bg-gradient-to-r from-[#F3F4F6] to-[#F3F4F6]/50 dark:from-[#1E2B42] dark:to-[#1E2B42]/70 rounded-xl border border-[rgba(8,31,61,0.1)] dark:border-[rgba(8,31,61,0.2)]">
                <FileText className="h-10 w-10 mr-4 text-[rgba(8,31,61,1)]" />
                <div className="flex-1">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-medium text-[#1C1C1C] dark:text-[#E8E8E8]">{selectedFile.name}</h3>
                      <p className="text-sm text-[#2F2F2F] dark:text-[#A3A3A3]">
                        {formatFileSize(selectedFile.size)} • {parsingStatement ? 'Reading statement…' : parsedData ? `${parsedData.length} transactions` : 'Analyzing...'}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedFile(null);
                        setParsedData(null);
                        setErrors([]);
                        setDetectedDateFormat(null);
                        setSelectedDateFormat('DD/MM/YYYY HH:mm'); // Reset to default
                        setStatementResult(null);
                        setSelectedAccountId('');
                        setReconciliationOverride(false);
                        setCurrencyAck(false);
                        setShowDateFormat(false);
                        setRememberedCategories(new Map());
                      }}
                      className="p-1 hover:bg-[#E5E7EB] dark:hover:bg-[#2D3748] rounded-md transition-colors"
                    >
                      <X className="h-4 w-4 text-[#2F2F2F] dark:text-[#A3A3A3]" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Account selection — for statements and CSVs alike */}
          {!accountId && parsedData && (
            <div className="mb-6 space-y-3">
              <div>
                <Label className="block text-sm font-medium text-[#1C1C1C] dark:text-[#E8E8E8] mb-2">
                  Import into account
                </Label>
                <Combobox
                  items={accounts.map((account: Account) => ({
                    value: account.id,
                    label: `${account.name}${account.currency ? ` (${account.currency})` : ''}`,
                  }))}
                  value={selectedAccountId}
                  onChange={(value) => {
                    setSelectedAccountId(value);
                    setCurrencyAck(false);
                    const account = accounts.find((a: Account) => a.id === value);
                    // Statement rows carry the account by name. Patch just
                    // that field — rebuilding rows would wipe review edits.
                    if (account && statementResult) {
                      setParsedData((prev: any[] | null) =>
                        prev ? prev.map((row) => ({ ...row, Account: account.name })) : prev
                      );
                    }
                  }}
                  placeholder={
                    statementResult
                      ? 'Select the account this statement belongs to'
                      : 'Select the account for these transactions'
                  }
                  className="h-11 rounded-xl"
                  contentStyle={{ zIndex: 9999 }}
                />
                {!statementResult && (
                  <p className="mt-1.5 text-xs text-[#2F2F2F] dark:text-[#A3A3A3]">
                    Used for every row. If your file has an Account column naming one of your
                    accounts, that wins for those rows.
                  </p>
                )}
              </div>

              {currencyMismatch && (
                <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
                  <AlertTriangle className="w-5 h-5 mt-0.5 text-amber-600 shrink-0" />
                  <div className="text-sm text-amber-800 dark:text-amber-200">
                    <p className="font-semibold">Currency doesn't match</p>
                    <p>
                      This statement is in {statementResult?.meta.currency}, but{' '}
                      {effectiveAccount?.name} is a {effectiveAccount?.currency} account.
                      Transactions take the account's currency, so the amounts would be recorded
                      as {effectiveAccount?.currency}.
                    </p>
                    <label className="flex items-center gap-2 mt-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={currencyAck}
                        onChange={(e) => setCurrencyAck(e.target.checked)}
                        className="rounded"
                      />
                      <span>Import anyway</span>
                    </label>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* PDF statement: reconciliation banner + review table */}
          {statementResult && (
            <div className="space-y-4 mb-6">
              {statementResult.reconciliation.ok ? (
                <div className="flex items-start gap-3 p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800">
                  <CheckCircle2 className="w-5 h-5 mt-0.5 text-green-600 shrink-0" />
                  <div className="text-sm text-green-800 dark:text-green-200">
                    <p className="font-semibold">Statement balances</p>
                    <p>
                      {statementResult.meta.bankName}
                      {statementResult.meta.cardLast4 ? ` •••• ${statementResult.meta.cardLast4}` : ''}
                      {statementResult.meta.statementDate ? `, issued ${statementResult.meta.statementDate}` : ''} —{' '}
                      {statementResult.reconciliation.rowCount} transactions, net{' '}
                      {(statementResult.reconciliation.parsedNetCents / 100).toFixed(2)}{' '}
                      {statementResult.meta.currency}, verified against the statement's printed totals.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800">
                  <AlertTriangle className="w-5 h-5 mt-0.5 text-red-600 shrink-0" />
                  <div className="text-sm text-red-800 dark:text-red-200">
                    <p className="font-semibold">Statement does not balance — import blocked</p>
                    <p>
                      The parsed transactions differ from the statement's printed totals by{' '}
                      {(Math.abs(statementResult.reconciliation.netDiscrepancyCents) / 100).toFixed(2)}{' '}
                      {statementResult.meta.currency}. Some transactions may be missing or misread.
                    </p>
                    <label className="flex items-center gap-2 mt-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={reconciliationOverride}
                        onChange={(e) => setReconciliationOverride(e.target.checked)}
                        className="rounded"
                      />
                      <span>I understand — let me import and fix it manually</span>
                    </label>
                  </div>
                </div>
              )}

              {/* Review & edit before committing */}
              {parsedData && parsedData.length > 0 && !importing && (
                <StatementReviewTable
                  rows={parsedData as any[]}
                  onRowsChange={(next) => setParsedData(next)}
                  categoryItems={categoryItems}
                  currency={statementResult.meta.currency}
                  onCategoryCorrected={(payee, categoryName) =>
                    setRememberedCategories((prev) => {
                      const next = new Map(prev);
                      next.set(payee.trim().toLowerCase(), { payee: payee.trim(), categoryName });
                      return next;
                    })
                  }
                  rememberCorrections={rememberCorrections}
                  onRememberChange={setRememberCorrections}
                />
              )}
            </div>
          )}

          <div className="space-y-4">
            {/* Date format: one line once detected, full picker only on demand */}
            {!statementResult && (
            <>
            {detectedDateFormat && !showDateFormat && (
              <div className="flex items-center justify-between gap-3 p-3 rounded-xl bg-[#F3F4F6] dark:bg-[#1E2B42]">
                <span className="text-sm text-[#2F2F2F] dark:text-[#A3A3A3]">
                  Dates read as{' '}
                  <span className="font-medium text-[#1C1C1C] dark:text-[#E8E8E8]">
                    {DATE_FORMATS.find((f) => f.id === selectedDateFormat)?.label}
                  </span>
                  {parsedData && parsedData.length > 0 && parsedData[0].Date
                    ? ` — e.g. ${parsedData[0].Date}`
                    : ''}
                </span>
                <button
                  onClick={() => setShowDateFormat(true)}
                  className="text-sm text-[#439476] hover:underline shrink-0"
                >
                  Change
                </button>
              </div>
            )}
            {(!detectedDateFormat || showDateFormat) && (
            <div>
              <Label htmlFor="dateFormat" className="block text-sm font-medium text-[#1C1C1C] dark:text-[#E8E8E8] mb-2">
                Date Format
                {detectedDateFormat && (
                  <span className="ml-2 text-xs text-[#439476] font-normal">
                    (Auto-detected: {DATE_FORMATS.find(f => f.id === detectedDateFormat)?.label})
                  </span>
                )}
              </Label>
              <Select
                value={selectedDateFormat}
                onValueChange={setSelectedDateFormat}
                disabled={importing}
              >
                <SelectTrigger className="w-full h-11 bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-lg backdrop-saturate-150 border-[rgba(8,31,61,0.2)] dark:border-[rgba(8,31,61,0.3)] rounded-xl shadow-sm relative z-10">
                  <SelectValue placeholder="Select date format" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px] overflow-y-auto" style={{zIndex: 9999}}>
                  <div className="grid gap-4 p-2">
                    {/* Date only formats */}
                    <div className="space-y-2">
                      <div className="px-2 py-1.5 text-xs font-medium text-[rgba(8,31,61,1)] dark:text-[rgba(8,31,61,0.8)] uppercase tracking-wider">
                        Date Only
                      </div>
                      {DATE_FORMATS.slice(0, 9).map((format) => (
                        <SelectItem
                          key={format.id}
                          value={format.id}
                          className="rounded-lg cursor-pointer transition-colors"
                        >
                          <div className="flex flex-col">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{format.label}</span>
                              {detectedDateFormat === format.id && (
                                <span className="text-xs px-2 py-0.5 bg-[#439476] text-white rounded-full">
                                  Auto-detected
                                </span>
                              )}
                            </div>
                            <span className="text-xs text-[#2F2F2F] dark:text-[#A3A3A3]">
                              Example: {format.example}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </div>

                    {/* 12-hour time formats */}
                    <div className="space-y-2">
                      <div className="px-2 py-1.5 text-xs font-medium text-[rgba(8,31,61,1)] dark:text-[rgba(8,31,61,0.8)] uppercase tracking-wider">
                        12-Hour Time
                      </div>
                      {DATE_FORMATS.slice(9, 14).map((format) => (
                        <SelectItem
                          key={format.id}
                          value={format.id}
                          className="rounded-lg cursor-pointer transition-colors"
                        >
                          <div className="flex flex-col">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{format.label}</span>
                              {detectedDateFormat === format.id && (
                                <span className="text-xs px-2 py-0.5 bg-[#439476] text-white rounded-full">
                                  Auto-detected
                                </span>
                              )}
                            </div>
                            <span className="text-xs text-[#2F2F2F] dark:text-[#A3A3A3]">
                              Example: {format.example}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </div>

                    {/* 24-hour time formats */}
                    <div className="space-y-2">
                      <div className="px-2 py-1.5 text-xs font-medium text-[rgba(8,31,61,1)] dark:text-[rgba(8,31,61,0.8)] uppercase tracking-wider">
                        24-Hour Time
                      </div>
                      {DATE_FORMATS.slice(14, 19).map((format) => (
                        <SelectItem
                          key={format.id}
                          value={format.id}
                          className="rounded-lg cursor-pointer transition-colors"
                        >
                          <div className="flex flex-col">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{format.label}</span>
                              {detectedDateFormat === format.id && (
                                <span className="text-xs px-2 py-0.5 bg-[#439476] text-white rounded-full">
                                  Auto-detected
                                </span>
                              )}
                            </div>
                            <span className="text-xs text-[#2F2F2F] dark:text-[#A3A3A3]">
                              Example: {format.example}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </div>

                    {/* ISO formats */}
                    <div className="space-y-2">
                      <div className="px-2 py-1.5 text-xs font-medium text-[rgba(8,31,61,1)] dark:text-[rgba(8,31,61,0.8)] uppercase tracking-wider">
                        ISO Format
                      </div>
                      {DATE_FORMATS.slice(19).map((format) => (
                        <SelectItem
                          key={format.id}
                          value={format.id}
                          className="rounded-lg cursor-pointer transition-colors"
                        >
                          <div className="flex flex-col">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{format.label}</span>
                              {detectedDateFormat === format.id && (
                                <span className="text-xs px-2 py-0.5 bg-[#439476] text-white rounded-full">
                                  Auto-detected
                                </span>
                              )}
                            </div>
                            <span className="text-xs text-[#2F2F2F] dark:text-[#A3A3A3]">
                              Example: {format.example}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </div>
                  </div>
                </SelectContent>
              </Select>
              <p className="mt-2 text-xs text-[#2F2F2F] dark:text-[#A3A3A3]">
                {detectedDateFormat ? (
                  <>
                    Format auto-detected from your CSV file. You can change it manually if needed.
                    {parsedData && parsedData.length > 0 && parsedData[0].Date && (
                      <span className="block mt-1 text-[#439476]">
                        Sample from your file: {parsedData[0].Date}
                      </span>
                    )}
                  </>
                ) : (
                  "Select the date format that matches your CSV file. The format will be auto-detected when you upload a file."
                )}
              </p>
            </div>
            )}
            </>
            )}

            {accounts.length === 0 && (
              <p className="text-sm italic text-[#2F2F2F] dark:text-[#A3A3A3]">
                No accounts available. Please create an account first.
              </p>
            )}

            {/* Advanced: settings most imports never need to touch */}
            <button
              onClick={() => setShowAdvanced((v) => !v)}
              className="flex items-center gap-1.5 text-sm text-[#2F2F2F] dark:text-[#A3A3A3] hover:text-[#439476] transition-colors"
            >
              {showAdvanced ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
              Advanced options
            </button>

            {showAdvanced && (
            <>
            <StyledModalSection
              title="Duplicate Check Settings"
              primaryColor="#8B5CF6"
            >
              <div className="space-y-4">
                <div>
                  <Label htmlFor="duplicateMode" className="block text-sm font-medium text-[#1C1C1C] dark:text-[#E8E8E8] mb-2">
                    Performance Mode
                  </Label>
                  <Select
                    value={duplicateCheckMode}
                    onValueChange={(value: DuplicateCheckMode) => setDuplicateCheckMode(value)}
                    disabled={importing}
                  >
                    <SelectTrigger className="w-full h-11 bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-lg backdrop-saturate-150 border-[rgba(8,31,61,0.2)] dark:border-[rgba(8,31,61,0.3)] rounded-xl shadow-sm">
                      <SelectValue placeholder="Select duplicate check mode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={DuplicateCheckMode.FAST} className="rounded-lg">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">🚀 Fast (Recommended)</span>
                            {parsedData && parsedData.length >= 100 && (
                              <span className="text-xs px-2 py-0.5 bg-green-500 text-white rounded-full">
                                Server-side
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-[#2F2F2F] dark:text-[#A3A3A3]">
                            Optimized for large imports (100+ transactions)
                          </span>
                        </div>
                      </SelectItem>
                      <SelectItem value={DuplicateCheckMode.BALANCED} className="rounded-lg">
                        <div className="flex flex-col">
                          <span className="font-medium">⚖️ Balanced</span>
                          <span className="text-xs text-[#2F2F2F] dark:text-[#A3A3A3]">
                            Good performance with thorough checking
                          </span>
                        </div>
                      </SelectItem>
                      <SelectItem value={DuplicateCheckMode.THOROUGH} className="rounded-lg">
                        <div className="flex flex-col">
                          <span className="font-medium">🔍 Thorough</span>
                          <span className="text-xs text-[#2F2F2F] dark:text-[#A3A3A3]">
                            Most accurate but slowest (best for small imports)
                          </span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="mt-1 text-xs text-[#2F2F2F] dark:text-[#A3A3A3]">
                    {duplicateCheckMode === DuplicateCheckMode.FAST && parsedData && parsedData.length >= 100 && (
                      "🚀 Will use server-side processing for optimal performance"
                    )}
                    {duplicateCheckMode === DuplicateCheckMode.FAST && parsedData && parsedData.length < 100 && (
                      "⚡ Fast mode optimized for your batch size"
                    )}
                    {duplicateCheckMode === DuplicateCheckMode.BALANCED && (
                      "⚖️ Balanced approach with good speed and accuracy"
                    )}
                    {duplicateCheckMode === DuplicateCheckMode.THOROUGH && (
                      "🔍 Most thorough checking including description similarity"
                    )}
                  </p>
                </div>
                
                <div className="flex items-center space-x-2">
                  <input
                    id="skipDuplicateCheck"
                    type="checkbox"
                    checked={skipDuplicateCheck}
                    onChange={(e) => setSkipDuplicateCheck(e.target.checked)}
                    disabled={importing}
                    className="w-4 h-4 text-[#439476] bg-gray-100 border-gray-300 rounded focus:ring-[#439476] dark:focus:ring-[#439476] dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                  />
                  <Label htmlFor="skipDuplicateCheck" className="text-sm text-[#1C1C1C] dark:text-[#E8E8E8]">
                    Skip duplicate checking completely (fastest import)
                  </Label>
                </div>
                
                {parsedData && parsedData.length >= 1000 && (
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-800">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <h4 className="font-medium text-blue-700 dark:text-blue-300">Large Import Detected</h4>
                        <p className="mt-1 text-sm text-blue-600 dark:text-blue-400">
                          {parsedData.length} transactions detected. Fast mode is recommended for optimal performance, 
                          or consider skipping duplicate checking if you're confident there are no duplicates.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </StyledModalSection>

            {!statementResult && (
            <StyledModalSection
              title="CSV Format Requirements"
              primaryColor="#439476"
            >
              <div className="flex justify-between items-start mb-2">
                <a 
                  href={templateUrl}
                  download="transactions_template.csv"
                  className="inline-flex items-center px-3 py-1 text-sm font-medium text-[rgba(8,31,61,1)] bg-[rgba(8,31,61,0.1)] hover:bg-[rgba(8,31,61,0.2)] rounded-lg transition-colors border border-[rgba(8,31,61,0.2)] dark:border-[rgba(8,31,61,0.3)]"
                >
                  <FileDown className="h-4 w-4 mr-1" />
                  Download Template
                </a>
              </div>
              <ul className="mt-2 text-sm text-[#2F2F2F] dark:text-[#A3A3A3] list-disc list-inside">
                <li>Date (format selectable above)</li>
                <li>Description (optional)</li>
                <li>Category (e.g., "Food & Dining - Groceries")</li>
                <li>Amount (positive for income, negative for expenses)</li>
                <li>Type (optional: "income" or "expense")</li>
                <li>Account name (must match with available accounts)</li>
                <li>Payee (optional)</li>
                <li>Tags (comma-separated, optional)</li>
                <li>Notes (optional)</li>
                <li>Supported delimiters: comma (,) or semicolon (;)</li>
              </ul>
            </StyledModalSection>
            )}
            </>
            )}

            {errors.length > 0 && (
              <StyledModalSection 
                title="Import Errors" 
                primaryColor="#A52A2A"
                secondaryColor="#A52A2A"
              >
                <div className="flex items-start">
                  <AlertTriangle className="h-5 w-5 mr-2 text-[#A52A2A] dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <ul className="mt-2 text-sm text-[#A52A2A]/90 dark:text-red-400/90 list-disc list-inside max-h-40 overflow-y-auto">
                    {errors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              </StyledModalSection>
            )}
          </div>
        </>
      )}
        
      {importResults.imported.length > 0 && (
        <StyledModalSection 
          title="Import Summary" 
          primaryColor="#439476"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              <span className="font-medium">Import completed</span>
            </div>
            <span className="text-sm text-gray-500">{new Date().toLocaleString()}</span>
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
              <div className="text-2xl font-bold">{importResults.imported.length}</div>
              <div className="text-sm text-gray-500">Transactions imported</div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
              <div className="text-2xl font-bold">{importResults.skipped.length}</div>
              <div className="text-sm text-gray-500">Transactions skipped</div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
              <div className="text-2xl font-bold">{importResults.total}</div>
              <div className="text-sm text-gray-500">Total processed</div>
            </div>
          </div>
          
          {unmatchedCategories.size > 0 && (
            <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-yellow-700">Unmatched Categories</h4>
                  <p className="mt-1 text-sm text-yellow-600">
                    The following categories could not be matched: {" "}
                    <span className="font-medium">{Array.from(unmatchedCategories).join(', ')}</span>
                  </p>
                </div>
              </div>
            </div>
          )}
        </StyledModalSection>
      )}
      
      {reportUrl && (
        <StyledModalSection 
          title="Import Report" 
          primaryColor="#439476"
        >
          <div className="flex justify-between items-center">
            <p className="text-sm text-[#439476]/90 dark:text-[#439476]/90">
              Download a detailed report of all transactions
            </p>
            <a 
              href={reportUrl}
              download={`transaction-import-report-${new Date().toISOString().split('T')[0]}.csv`}
              className="inline-flex items-center px-3 py-1 text-sm font-medium text-[rgba(8,31,61,1)] bg-[rgba(8,31,61,0.1)] hover:bg-[rgba(8,31,61,0.2)] rounded-lg transition-colors border border-[rgba(8,31,61,0.2)] dark:border-[rgba(8,31,61,0.3)]"
            >
              <Download className="h-4 w-4 mr-2" />
              Download Report
            </a>
          </div>
        </StyledModalSection>
      )}
      
      {importCompleted && (
        <div className="flex justify-end gap-3 mt-4">
          <StyledModalButton variant="secondary" onClick={onClose}>
            Close
          </StyledModalButton>
          <StyledModalButton 
            onClick={() => {
              // Reset state to start a new import
              setSelectedFile(null);
              setParsedData(null);
              setImportResults({ imported: [], skipped: [], total: 0 });
              setUnmatchedCategories(new Set());
              setInvalidRuleCategoryIds(new Set());
              setImportCompleted(false);
              setDetectedDateFormat(null);
              setSelectedDateFormat('DD/MM/YYYY HH:mm'); // Reset to default
              setStatementResult(null);
              setSelectedAccountId('');
              setReconciliationOverride(false);
              setCurrencyAck(false);
              setShowDateFormat(false);
              setRememberedCategories(new Map());
            }}
          >
            Import More Transactions
          </StyledModalButton>
        </div>
      )}
    </StyledModal>
  );
}
